<script setup lang="ts">
import _ from 'lodash'
import { arrayify, clamp, ordinal, spellAreaIcon } from '../util'
import { type PlayerSpellcastingData, type Ability, type CharacterSheetData, statnames } from '../statblock/statblock'
import { totalLevel } from '../statblock/statblock'
import { joinDice, resolveDice } from '~/assets/dice/diceEval'
import { type ResolvedSpell, resolveSpell, spellLists } from '~/assets/spells/spellData'

const cantripDice = [1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4]

const progression: Record<number, number[]> = {
  1: [0, 2],
  2: [0, 3],
  3: [0, 4, 2],
  4: [0, 4, 3],
  5: [0, 4, 3, 2],
  6: [0, 4, 3, 3],
  7: [0, 4, 3, 3, 1],
  8: [0, 4, 3, 3, 2],
  9: [0, 4, 3, 3, 3, 1],
  10: [0, 4, 3, 3, 3, 2],
  11: [0, 4, 3, 3, 3, 2, 1],
  12: [0, 4, 3, 3, 3, 2, 1],
  13: [0, 4, 3, 3, 3, 2, 1, 1],
  14: [0, 4, 3, 3, 3, 2, 1, 1],
  15: [0, 4, 3, 3, 3, 2, 1, 1, 1],
  16: [0, 4, 3, 3, 3, 2, 1, 1, 1],
  17: [0, 4, 3, 3, 3, 2, 1, 1, 1, 1],
  18: [0, 4, 3, 3, 3, 3, 1, 1, 1, 1],
  19: [0, 4, 3, 3, 3, 3, 2, 1, 1, 1],
  20: [0, 4, 3, 3, 3, 3, 2, 2, 1, 1],
}

const progressionWarlock: Record<number, number[]> = {
  1: [0, 1],
  2: [0, 2],
  3: [0, 0, 2],
  4: [0, 0, 2],
  5: [0, 0, 0, 2],
  6: [0, 0, 0, 2],
  7: [0, 0, 0, 0, 2],
  8: [0, 0, 0, 0, 2],
  9: [0, 0, 0, 0, 0, 2],
  10: [0, 0, 0, 0, 0, 2],
  11: [0, 0, 0, 0, 0, 3, 1],
  12: [0, 0, 0, 0, 0, 3, 1],
  13: [0, 0, 0, 0, 0, 3, 1, 1],
  14: [0, 0, 0, 0, 0, 3, 1, 1],
  15: [0, 0, 0, 0, 0, 3, 1, 1, 1],
  16: [0, 0, 0, 0, 0, 3, 1, 1, 1],
  17: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
  18: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
  19: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
  20: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
}

const cantripsFull = [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2]
const cantripsHalf = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2]
const cantripsThird = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
const cantripsNone = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]

const prepsBard = [0, 4, 5, 6, 7, 9, 10, 11, 12, 14, 15, 16, 16, 17, 17, 18, 18, 19, 20, 21, 22]
const prepsCleric = [0, 4, 5, 6, 7, 9, 10, 11, 12, 14, 15, 16, 16, 17, 17, 18, 18, 19, 20, 21, 22]
const prepsDruid = [0, 4, 5, 6, 7, 9, 10, 11, 12, 14, 15, 16, 16, 17, 17, 18, 18, 19, 20, 21, 22]
const prepsSorcerer = [0, 2, 4, 6, 7, 9, 10, 11, 12, 14, 15, 16, 16, 17, 17, 18, 18, 19, 20, 21, 22]
const prepsWizard = [0, 4, 5, 6, 7, 9, 10, 11, 12, 14, 15, 16, 16, 17, 18, 19, 21, 22, 23, 24, 25]
const prepsWarlock = [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 11, 11, 12, 12, 13, 13, 14, 14, 15, 15]
const prepsHalf = [0, 2, 3, 4, 5, 6, 6, 7, 7, 9, 9, 10, 10, 11, 11, 12, 12, 14, 14, 15, 15]
const prepsThird = [0, 0, 0, 3, 4, 4, 4, 5, 6, 6, 7, 8, 8, 9, 10, 10, 11, 11, 11, 12, 13]

const abilities = new Set(['str','dex','con','int','wis','cha'])

const props = defineProps<{
  stats: CharacterSheetData
  data: PlayerSpellcastingData
}>()

interface ClassData {
  // the progression rate of this class
  progression: string,
  // the spellcasting ability of this class
  stat: Ability,
  // the number of preps per level
  preps: number[],
  // the number of base cantrips
  baseCantrips: number
  // the number of cantrips per level
  cantrips: number[],
  // if this class can cast rituals without preparing them
  rituals?: boolean
  // main class name, for spellcaster subclasses
  cls?: string
}
const classes: Record<string, ClassData> = {
  artificer: { progression: 'half', stat: 'int', preps: prepsHalf, baseCantrips: 2, cantrips: cantripsHalf },
  bard: { progression: 'full', stat: 'cha', preps: prepsBard, baseCantrips: 2, cantrips: cantripsFull },
  woad: { progression: 'third', stat: 'wis', cls: 'barbarian', preps: prepsThird, baseCantrips: 2, cantrips: cantripsThird },
  cleric: { progression: 'full', stat: 'wis', preps: prepsCleric, baseCantrips: 3, cantrips: cantripsFull },
  druid: { progression: 'full', stat: 'wis', preps: prepsDruid, baseCantrips: 2, cantrips: cantripsFull },
  "eldritch knight": { progression: 'third', stat: 'int', cls: 'fighter', preps: prepsThird, baseCantrips: 2, cantrips: cantripsThird },
  paladin: { progression: 'half', stat: 'cha', preps: prepsHalf, baseCantrips: 0, cantrips: cantripsNone },
  ranger: { progression: 'half', stat: 'wis', preps: prepsHalf, baseCantrips: 0, cantrips: cantripsNone },
  "mystic arts": { progression: 'third', stat: 'wis', cls: 'monk', preps: prepsThird, baseCantrips: 2, cantrips: cantripsThird },
  "arcane trickster": { progression: 'third', stat: 'int', cls: 'rogue', preps: prepsThird, baseCantrips: 2, cantrips: cantripsThird },
  inquisitor: { progression: 'third', stat: 'wis',  cls: 'rogue', preps: prepsThird, baseCantrips: 2, cantrips: cantripsThird },
  spellsword: { progression: 'half', stat: 'int', preps: prepsHalf, baseCantrips: 2, cantrips: cantripsFull },
  sorcerer: { progression: 'full', stat: 'cha', preps: prepsSorcerer, baseCantrips: 4, cantrips: cantripsFull },
  warlock: { progression: 'warlock', stat: 'cha', preps: prepsWarlock, baseCantrips: 2, cantrips: cantripsFull },
  wizard: { progression: 'full', stat: 'int', rituals: true, preps: prepsWizard, baseCantrips: 3, cantrips: cantripsFull },
}

const resolveClassData = (data: PlayerSpellcastingData) => classes[data.class.toLowerCase().trim()]
const classData = computed(() => resolveClassData(props.data))

const stat = computed(() => classData.value.stat)

const spellAttackModifier = computed(() => {
  const bonus = props.data.bonusAttacks ?? ''
  return joinDice(stat.value, 'prof', bonus)
})

const spellSaveDC = computed(() => {
  const bonus = props.data.bonusSaves ?? ''
  return joinDice(8, stat.value, 'prof', bonus, 'exhdc')
})

const mods = useMods()

const resolveLevel = (data: PlayerSpellcastingData) => {
  const classData = resolveClassData(data)
  return resolveDice(classData.cls ?? data.class, mods(), true)
}

// only level in this spellcasting
const classLevel = computed(() => resolveLevel(props.data))

const maxSpellLevel = computed(() => {
  const lvl = classLevel.value
  const prog = classData.value.progression

  if (prog === 'half') {
    return Math.ceil(lvl / 4)
  } else if (prog === 'third') {
    return Math.ceil(lvl / 6)
  }
  return Math.ceil(lvl / 2)
})

const statText = computed(() => statnames[classData.value.stat])

const casterLevel = computed(() => {
  const resolve = (data: PlayerSpellcastingData) => {
    const level = resolveLevel(data)
    const cls = resolveClassData(data)
    if (cls.progression === 'half') {
      return Math.ceil(level / 2)
    } else if (cls.progression === 'third') {
      return Math.ceil(level / 3)
    } else {
      return level
    }
  }
  return _.sum(
    arrayify(props.stats.spells!)
      .map(resolve),
  )
})

const spellSlots = computed(() => {
  if (classData.value.progression === 'warlock') {
    return progressionWarlock[casterLevel.value]
  }
  return progression[casterLevel.value]
})

const spellList = computed<Set<string>>(() => {
  const spellbook = new Set(props.data.spellbook ?? [])
  let list = props.data.class.toLowerCase().trim()
  if (list === 'wizard') {
    return spellbook.union(spellLists['wizardCantrips'])
  }

  if (list == 'inquisitor') {
    list = 'cleric'
  } else if (list === 'arcane trickster' || list === 'eldritch knight') {
    list = 'wizard'
  } else if (list === 'woad') {
    list = 'druid'
  } else if (list === 'mystic arts') {
    list = 'sorcerer'
  }
  return spellbook.union(spellLists[list] ?? new Set([]))
})

const isPreparing = ref(false)

interface PageData {
  // Name of the spell currently being concentrated on
  concentration?: string,
  // { [class]: { [spell]: true } }
  preparations: Record<string, Record<string, boolean>>
  cantrips: Record<string, Record<string, boolean>>
}
const pageData = usePageData<PageData>({ preparations: {}, cantrips: {} })
const preparations = computed(() => pageData.value.preparations[props.data.class] ?? {})
const isPrepared = (spell: ResolvedSpell) => props.data.always?.includes(spell.name) || preparations.value[spell.name] || cantrips.value[spell.name]

const numPrepared = computed(() => Object.values(preparations.value).filter(Boolean).length)
const maxPreparations = computed(() => {
  const base = classData.value.preps[classLevel.value] ?? 0
  const bonus = props.data.bonusPreparations ? resolveDice(props.data.bonusPreparations, mods(), true) : 0
  return clamp(base + bonus, 0, 100)
})

const cantrips = computed(() => pageData.value.cantrips?.[props.data.class] ?? {})
const numCantrips = computed(() => Object.values(cantrips.value).filter(Boolean).length)
const maxCantrips = computed(() => {
  const { baseCantrips, cantrips } = classData.value
  const bonus = props.data.bonusCantrips ? resolveDice(props.data.bonusCantrips, mods(), true) : 0
  const scaling = cantrips[classLevel.value] ?? 0
  return baseCantrips + scaling + bonus
})

const setPreparation = (spell: ResolvedSpell, ev: Event) => {
  ev.preventDefault()
  ev.stopPropagation()
  const active = ((ev as InputEvent).target as HTMLInputElement)?.checked
  const preps = spell.level === 0 ? numCantrips.value : numPrepared.value
  const maxPreps = spell.level === 0 ? maxCantrips.value : maxPreparations.value
  if (active && preps >= maxPreps) return

  const prevKey = spell.level === 0 ? 'cantrips' : 'preparations'
  const prevObj = spell.level === 0 ? cantrips.value : preparations.value
  // remove all spells not on the spell list from the prepared set
  const prev = Object.fromEntries(Object.entries(prevObj).filter(([name, _]) => spellList.value.has(name)))

  pageData.value = {
    ...pageData.value,
    [prevKey]: {
      ...pageData.value[prevKey],
      [props.data.class]: {
        ...prev,
        [spell.name]: active
      }
    }
  }
}

const disablePreparation = (spell: ResolvedSpell) => {
  const preps = spell.level === 0 ? numCantrips.value : numPrepared.value
  const maxPreps = spell.level === 0 ? maxCantrips.value : maxPreparations.value
  return (!isPrepared(spell) && (preps >= maxPreps)) || props.data.always?.includes(spell.name)
}

const toggleConcentration = (spell: ResolvedSpell) => {
  if (!spell.concentration) return
  const concentration = spell.name === pageData.value.concentration ? undefined : spell.name
  pageData.value = { ...pageData.value, concentration }
}

onRest('lr', () => {
  if (pageData.value.concentration) {
    pageData.value = { ...pageData.value, concentration: undefined }
  }
})

const spellsByLevel = computed(() => {
  const always = props.data.always?.map((name) => resolveSpell(name, props.data.overrides?.[name])).filter(x => x !== null) ?? []
  const showRituals = !!classData.value.rituals
  const prepared = [...spellList.value]
    .map((name) => resolveSpell(name, props.data.overrides?.[name]))
    .filter((x) => x !== null)
    .filter(spell => {
      if (isPreparing.value) {
        return spell.level <= maxSpellLevel.value && !props.data.always?.includes(spell.name)
      } else {
        return cantrips.value[spell.name] || preparations.value[spell.name] || (showRituals && spell.ritual)
      }
    })
  const allSpells = [...always, ...prepared]

  const spells = spellSlots.value
    .map((slots, level) => ({
      level,
      slots: slots + (props.data.bonusSlots?.[level + 1] ?? 0),
      spells: allSpells.filter(s => s.level === level).sort((a, b) => a.name.localeCompare(b.name))
    }))
    .filter((lvl) => (lvl.level === 0 || lvl.slots > 0) || lvl.spells.length > 0)

  return spells
})

const onShortRest = (level: number) => {
  if (classData.value.progression === 'warlock' && level <= 5) {
    return 'reset'
  }
  return ''
}

const extraMods = (lvl: number) => {
  if (lvl === 0) {
    const level = totalLevel(props.stats.level)
    return { lvl: cantripDice[level] ?? 1 }
  } else {
    return { lvl }
  }
}

const resetAll = () => {
  if (confirm("Reset all preparations?")) {
    pageData.value = { preparations: {}, cantrips: {} }
  }
}
</script>

<template>
  <div class="spells" :class="{preparing: isPreparing}">
    <template v-for="{level, slots, spells} in spellsByLevel" :key="level">
      <div class="cell level-header">
        <span v-if="level === 0" class="lvl title-font">Cantrips<span v-if="isPreparing || numCantrips !== maxCantrips">&nbsp;({{numCantrips}}/{{maxCantrips}})</span></span>
        <span v-else class="lvl title-font">{{ ordinal(level) }}-level spells</span>
        <Counter v-if="!data.hideSlots && slots > 0" :uses="slots" noparens :name="`ss${level}`" :sr="onShortRest(level)" />
      </div>
      <div v-for="spell in spells" :key="spell.name" class="spell-row" :class="{ concentration: pageData.concentration === spell.name, unprepared: !data.always?.includes(spell.name) && !preparations[spell.name] && !cantrips[spell.name] }">
        <div class="cell icons">
          <Icon v-if="!isPreparing" :name="spell.school" />
          <input v-else type="checkbox" :checked="isPrepared(spell)" :disabled="disablePreparation(spell)" @change="setPreparation(spell, $event)" />
        </div>
        <div class="cell name vertical left">
          <WikiLink :wiki="spell.wiki" :label-override="spell.alias ?? spell.name" :title="spell.alias ? spell.name : undefined"></WikiLink>
          <span class="secondary">
            <em class="school">{{ spell.school }}</em> &middot; <span class="components">{{spell.components}}</span>
          </span>
        </div>
        <div class="cell casttime">{{ spell.cast }}<Icon v-if="spell.ritual" name="fa6-regular:registered" /></div>
        <div class="cell duration" @dblclick="toggleConcentration(spell)">
          {{ spell.duration }}
          <Icon v-if="spell.concentration" name="fa6-regular:copyright" />
        </div>
        <div class="cell range vertical">
          <span>{{ spell.range }}</span>
          <span v-for="(feet, area) in spell.area" :key="area">
            ({{ feet }}ft <Icon :name="spellAreaIcon(area)" />)
          </span>
        </div>
        <div class="cell effect vertical">
          <template v-for="effect in spell.fx" :key="effect">
            <Dice v-if="effect === 'atk'" :dice="spellAttackModifier" :label="spell.name"> to hit</Dice>
            <span v-else-if="abilities.has(effect as string)" >{{ (effect as string).toUpperCase() }} <Dice value :dice="spellSaveDC" /></span>
            <Dice v-else :dice="`${effect}`.replace('mod', classData.stat)" :extra-mods="extraMods(level)" :label="spell.name" value="auto" />
          </template>
        </div>
      </div>
    </template>
  </div>
  <div class="buttons">
    <button class="prepare-button" @click="isPreparing = !isPreparing">
      <span v-if="!isPreparing">Change Preparations</span>
      <span v-else>Finish</span>
      <span v-if="isPreparing || numPrepared !== maxPreparations">({{numPrepared}}/{{maxPreparations}})</span>
    </button>
    <button class="prepare-button" @click="resetAll">
      Reset
    </button>
  </div>
  <p>
    <strong>Spellcasting</strong>: You are a {{ ordinal(classLevel) }}-level spellcaster. Your spellcasting attribute is {{ statText }} (DC <Dice value :dice="spellSaveDC" />, <Dice :dice="spellAttackModifier" > to hit</Dice>). You can prepare up to {{ maxPreparations }} spells<span v-if="classData.rituals">, and you can cast rituals from your spellbook</span>.
  </p>
</template>

<style scoped lang="postcss">
.spells {
  --cell-padding-h: 0.25em;
  min-width: calc(100% + 2 * var(--cell-padding-h));
  max-width: calc(100% + 2 * var(--cell-padding-h));
  overflow-x: scroll;
  margin: 0 calc(-1 * var(--cell-padding-h)) 0.75rem;

  display: grid;
  grid-template-columns: max-content 1fr max-content max-content max-content max-content;

  .cell {
    padding: .15em var(--cell-padding-h);
    display: flex;

    &.vertical {
      flex-direction: column;
      align-items: center;
      justify-content: center;

      &.left {
        align-items: flex-start;
      }
    }
  }

  .icons {
    align-items: center;
    padding-right: 0;
  }

  .spell-row {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;

    &.unprepared {
      opacity: 0.85;
    }

    &:hover {
      background: var(--bg-primary);
      color: var(--text-highlight);
    }

    &.concentration {
      --primary: var(--secondary);
      background: var(--bg-secondary);
    }
  }

  &.preparing .unprepared {
    opacity: .7;
  }

  .name {
    text-align: left;
    line-height: 1.1;

    .secondary {
      font-size: .8em;
      color: var(--text);

      .school {
        text-transform: capitalize;
      }

      .components {
        text-transform: uppercase;
      }
    }
  }

  .casttime, .duration, .range, .icon {
    justify-content: center;
    align-items: center;
    user-select: none;
  }

  .casttime {
    text-transform: uppercase;
  }

  .duration, .range {
    text-transform: capitalize;
  }

  .range, .effect {
    line-height: 1.2;
    font-size: 0.9em;
  }

  .casttime, .duration {
    font-weight: 500;
    .icon {
      margin-left: 0.5ch;
    }
  }

  .level-header {
    grid-column: 1 / -1;
    display: flex;
    justify-content: space-between;
    padding: 0 0.25em;

    .lvl {
      color: var(--text-highlight);
      margin-top: .25rem;
      font-size: .9em;
    }
  }
}

.buttons {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;

  button {
    padding: 0.25em 0.75em;
    font-size: .8em;
    margin: 0;
  }
}
</style>
